<script lang="ts">
import { AccessLevel } from '@/modules/common/enums/access-levels.enum'
import { Vue, Component } from 'vue-property-decorator'
import GtrSuper from '@/modules/common/components/mixins/gtr-super.mixin'
import { mapGetters, mapState } from 'vuex'
import Container from 'typedi'
import ErrorHandlerService from '@/modules/common/services/error-handler.service'

@Component({
  name: 'GtrLearningLayout',
  computed: {
    ...mapState('common', ['noticeDisplayed']),
    ...mapState('company', ['company']),
    ...mapGetters('media', ['defaultFonts', 'fontList'])
  }
})
export default class GtrLearningLayout extends GtrSuper {
  /** vuex bindings */
  noticeDisplayed!: boolean;

  /** Component state */
  mountComplete = false

  company!: Record<string, any>
  defaultFonts!: Array<Record<string, any>>;
  fontList!: Array<Record<string, any>>;
  AccessLevel = AccessLevel

  /** Component methods */
  async mounted () {
    try {
      if (!this.$store.state.security.currentUser) {
        await this.$store.dispatch('security/getCurrentUser')
      } else if (!this.$store.state.company.company) {
        await this.$store.dispatch('company/getCompany', {
          uuid: this.$route.params.uuid
        })
      }
      this.mountComplete = true
    } catch (error) {
      Container.get(ErrorHandlerService).error(error)
    }
  }

  navigateTo (route: any) {
    this.$router.push(route).catch(error => Container.get(ErrorHandlerService).error(error))
  }

  hasPermission (permission?: string) {
    if (permission) {
      return this.$hasPermission(permission)
    } else {
      return true
    }
  }

  get amsSource () {
    if (this.company?.lms_settings?.ams_integration?.ams_source) {
      return this.company.lms_settings.ams_integration.ams_source
    }
    return ''
  }

  get menuItems () {
    return [
      {
        title: 'Dashboard',
        icon: 'mdi-view-dashboard',
        route: { name: 'learning.dashboard', meta: { visible: true } }
      },
      // {
      //   title: 'People',
      //   icon: 'mdi-account-multiple',
      //   children: [
      //     { title: 'Learners', icon: 'mdi-account', route: { name: 'learning.people.learners' } },
      //     { title: 'Groups', icon: 'mdi-account-multiple', route: { name: 'learning.people.groups', meta: { permission: 'learning.groups.view' } } }
      //     { title: 'Instructors', route: { name: 'learning.people.instructors' } },
      //     { title: 'Roles', route: { name: 'learning.people.roles' } }
      //   ]
      // },
      {
        title: 'Groups',
        icon: 'mdi-account-multiple',
        route: { name: 'learning.people.groups', meta: { permission: 'learning.groups.view', visible: this.amsSource !== 'wild_apricot' } }
      },
      // {
      //   title: 'Learning Content',
      //   icon: 'mdi-book-open-variant',
      //   children: [
      //     { title: 'Courses', icon: 'mdi-google-classroom', route: { name: 'learning.content.courses', meta: { permission: 'learning.courses.view' } } },
      //     { title: 'Assessments', route: { name: 'learning.content.assessments' } },
      //     { title: 'Certificates', icon: 'mdi-certificate', route: { name: 'learning.content.certificates' } },
      //     { title: 'CEU Management', icon: 'mdi-check-decagram', route: { name: 'learning.content.ceu_management' } }
      //   ]
      // },
      {
        title: 'Courses',
        icon: 'mdi-google-classroom',
        route: { name: 'learning.content.courses', meta: { permission: 'learning.courses.view', visible: true } }
      },
      {
        title: 'Certificates',
        icon: 'mdi-certificate',
        route: { name: 'learning.content.certificates', meta: { visible: true } }
      },
      {
        title: 'CEU Management',
        icon: 'mdi-check-decagram',
        route: { name: 'learning.content.ceu_management', meta: { visible: true } }
      },
      // {
      //   title: 'Emails',
      //   route: { name: 'learning.emails' }
      // },
      // {
      //   title: 'Reports',
      //   icon: 'mdi-chart-bar',
      //   route: { name: 'learning.reports' }
      // },
      {
        title: 'Settings',
        icon: 'mdi-cog',
        route: { name: 'learning.settings', meta: { visible: true } }
      }
    ]
  }
}
</script>

<template>
  <v-app id="inspire">
    <link v-for="(font, index) in defaultFonts" :key="`${font}-${index}`" rel="stylesheet" :href="`https://fonts.googleapis.com/css2?family=${font}:ital,wght@0,400;0,700;1,400;1,700&display=swap`" type="text/css" />
    <link v-for="(font, index) in fontList" :key="`${font}-${index}`" rel="stylesheet" :href="`${font.files.css}`" type="text/css" />
    <!-- App Bar -->
    <v-app-bar
      app
      color="var(--base-white)"
      clipped-left
      dense
      flat
      height="100px"
      :class='{ noticeDisplayed }'
      id="top-nav"
    >
      <gtr-feedback/>
      <img
        :src="require('@/assets/img/Momentive-A2Z-Events-Logo-Color.svg')"
        id="a2z-events-logo"
        alt="Momentive A2Z Events logo"
        class="mr-5"
      />
      <v-toolbar-items
        v-for="item in adminDrawerMenuItems"
        :key="item.text"
        class="hidden-lg-and-down"
        :style="item.route.meta.access_level && !$hasRoles(item.route.meta.access_level) ? 'display: none;' : ''"
      >
        <v-btn
          :to="{ name: item.route.name }"
          class="nav-link menu-item main-header-text"
          text
          color="var(--purple-grey-200)"
        >
          {{ item.text }}
        </v-btn>
      </v-toolbar-items>
      <v-toolbar-items class="hidden-xs-only">
        <v-btn
          link
          href="https://gtrnow.zendesk.com"
          class="nav-link menu-item main-header-text"
          target="_blank"
          text
          color="var(--purple-grey-200)"
        >
          Documentation
        </v-btn>
      </v-toolbar-items>
      <v-spacer></v-spacer>
      <v-menu v-if="impersonator" offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn @click="stopImpersonating" text link class="btn-link gtr-text-capitalize" v-bind="attrs" v-on="on" :ripple="false">
                <span class="d-block">
                  <small>Stop Impersonating<br />(Back to {{ impersonator.name }})</small>
                </span>
          </v-btn>
        </template>
      </v-menu>
      <v-app-bar-nav-icon
        v-if="$hasRoles([AccessLevel.SUPER_ADMIN, AccessLevel.RESELLER_ADMIN])"
        @click.stop="handleAdminNavDrawer"
        class="hidden-xl-only hidden-xs-only"
      ></v-app-bar-nav-icon>
      <v-menu offset-y v-if="$hasPermission('general.support.view')">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            large
            v-bind="attrs"
            v-on="on"
            title="Support"
            :to="{ name: 'level-two.company.support' }"
            class="hidden-xs-only"
          >
            <v-avatar size="38" item>
              <v-img :src="require('@/assets/img/modules/support.svg')" />
            </v-avatar>
          </v-btn>
        </template>
      </v-menu>
      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon large v-bind="attrs" v-on="on">
            <v-avatar v-if="userLoggedIn() && userLoggedIn().logo" size="32px" item>
              <v-img :src="signInUserLogo()" :alt="userLoggedIn().name"></v-img>
            </v-avatar>
            <v-avatar v-if="userLoggedIn() && !userLoggedIn().logo" size="38" item color="var(--neon-green-100)">
              <span class="white--text headline">{{ signInUserLogo() }}</span>
            </v-avatar>
          </v-btn>
        </template>
        <v-list class="pt-0 pb-0">
          <v-list-item class="item" link @click="profile">
            <v-icon class="mr-1 main-header-text" dense small>mdi-account-edit-outline</v-icon>
            <v-list-item-title class="fs-13 main-header-text">Profile</v-list-item-title>
          </v-list-item>
          <v-list-item class="item hidden-sm-and-up" link href="https://gtrnow.zendesk.com" target="_blank">
            <v-icon class="mr-1" dense small>mdi-book-open-outline</v-icon>
            <v-list-item-title class="fs-13">Documentation</v-list-item-title>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item class="item" link @click="logout">
            <v-icon class="mr-1 main-header-text" dense small>mdi-logout-variant</v-icon>
            <v-list-item-title class="fs-13 main-header-text">Logout</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>
    <!-- End App Bar -->

    <!-- Content -->
    <slot v-if="mountComplete"></slot>
    <!-- End Content -->

    <!-- Admin Sidebar -->
    <v-navigation-drawer
      v-model="adminNavDrawer"
      absolute
      temporary
      right
      height="auto"
      class="hidden-xs-only"
    >
      <v-list>
        <v-list-item
          v-for="item in adminDrawerMenuItems"
          :key="item.text"
          link
          :to="{ name: item.route.name }"
          class="nav-link menu-item"
          :style="item.route.meta.access_level && !$hasRoles(item.route.meta.access_level) ? 'display: none;' : ''"
        >
          <v-list-item-content>
            <v-list-item-title>{{ item.text }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <!-- Sidebar -->
    <v-navigation-drawer
      :permanent="true"
      :mini-variant="mini"
      :right="false"
      color="var(--purple-grey-200)"
      clipped
      app
      :class="{ 'notice-displayed': noticeDisplayed, drawer: noticeDisplayed }"
      class="side-nav"
    >
      <v-list-item link :ripple="false" class="pa-0 side-nav-back-btn">
        <v-list-item-content
          class="justify-center gtr-f-p pa-0"
        >
          <a
            class="
              side-nav-back-btn-link
              btn-link
              text-center
              gtr-text-capitalize
            "
            @click="
              navigateTo({
                name: 'level-two.company.dashboard',
                params: { uuid: $route.params.uuid },
              })
            "
          ><v-icon
            color="var(--grey-100)"
            title="Back to Company"
          >
            mdi-chevron-left
          </v-icon>
            <span v-if="!mini">Back to Company Dashboard</span></a
          >
        </v-list-item-content>
      </v-list-item>
      <v-divider></v-divider>
      <v-list-item
        :disabled="mini"
        link
        :ripple="false"
      >
        <v-list-item-content class="justify-center gtr-f-p">
          <v-icon
            :disabled="mini"
            color="var(--base-white)"
          >
            mdi-menu
          </v-icon>
        </v-list-item-content>
      </v-list-item>
      <v-list class="pt-0">
        <template v-for='(mod, key) in menuItems'>
          <v-list-group dense v-if="mod.children && mod.route.meta.visible" :key="key" active-class="active-group">
            <template #activator>
              <v-list-item-action>
                <v-icon class="side-nav-icon">{{ mod.icon }}</v-icon>
              </v-list-item-action>
              <v-list-item-title class="side-nav-name">{{ mod.title }}</v-list-item-title>
            </template>
            <template>
              <template v-for="child in mod.children">
                <v-list-item
                  :key="child.route.name"
                  :to="child.route"
                  link
                  class="side-nav-item sub-nav-item"
                  v-if="hasPermission(child?.route?.meta?.permission)"
                >
                  <v-list-item-action>
                    <v-icon class="side-nav-icon">{{ child.icon }}</v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title class="side-nav-name">
                      {{ child.title }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </template>
          </v-list-group>
          <v-list-item
            v-else-if="!mod.children && mod.route.meta.visible"
            :key="key"
            :to="mod.route"
            link
            class="side-nav-item"
          >
            <v-list-item-action>
              <v-icon class="side-nav-icon">{{ mod.icon }}</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title class="side-nav-name">
                {{ mod.title }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-list>
    </v-navigation-drawer>
  </v-app>
</template>
<!--TODO: this can be cleaned up-->
<style lang="scss">
.v-list--two-line .v-list-item,
.v-list-item--two-line {
  min-height: 70px !important;
}
.noticeDisplayed {
  margin-top: 100px !important;
}
.main-header-text {
  color: var(--purple-grey-200);
}
.side-nav-name {
  color: var(--grey-50);
  font-size: 13px;
  line-height: inherit !important;
}
.v-list-item--active.side-nav-item {
  color: var(--base-white);
}
a.side-nav-item.v-list-item--active.v-list-item.v-list-item--link.theme--light::before {
  background-color: transparent;
}
a.side-nav-item.v-list-item--active.v-list-item.v-list-item--link.theme--light .v-list-item__content .v-list-item__title.side-nav-name {
  color: var(--base-white);
}
//.v-list-item.side-nav-item:hover {
//  background-color: var(--purple-grey-100);
//}
.v-list-item.sub-nav-item {
  padding-left: 30px;
}
.v-icon.side-nav-icon {
  color: var(--grey-50);
}
.v-icon.notranslate.mdi.mdi-chevron-down.theme--light {
  color: var(--grey-50);
}
//.v-list-group__header.v-list-item.v-list-item--active.v-list-item--link.theme--light {
//  background-color: var(--purple-grey-100);
//  color: var(--base-white);
//}
//.v-list-group__header.v-list-item.v-list-item--link.theme--light:hover {
//  background-color: var(--purple-grey-100);
//}
.active-group .v-list-item__title {
  color: var(--base-white);
}
#top-nav.v-app-bar {
  border-bottom: 1px solid var(--grey-25) !important;
}
</style>
